// « Mon compte », dans les Réglages de Radar : ce qui touche au compte plutôt
// qu'aux offres.

import { useState } from 'react'
import { ApiError, post } from '../lib/api'
import { useT } from '../lib/i18n'
import { ActionButton } from './common'

/** Doit valoir `LONGUEUR_MOT_DE_PASSE` de `web/auth.ts` (cf. la même
 *  constante dans `pages/ChangerMotDePasse.tsx`). Le serveur reste juge. */
const LONGUEUR_MINIMALE = 12

/**
 * Changer son mot de passe, volontairement (issue #174). La route existait
 * (`POST /api/auth/password` avec le mot de passe actuel) ; l'écran, lui,
 * n'existait que pour un mot de passe provisoire. Le serveur ferme toutes les
 * sessions du compte et rend une neuve à celle-ci : on reste connecté ici, les
 * autres appareils et les liens déjà envoyés par mail sont fermés.
 */
export function ChangerMotDePasseVolontaire({ onChange }: { onChange: () => void }) {
  const t = useT()
  const [ouvert, setOuvert] = useState(false)
  const [actuel, setActuel] = useState('')
  const [nouveau, setNouveau] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)

  if (!ouvert) {
    return (
      <div className="cfg-sous">
        <span>
          {t('compte.motDePasse')}{' '}
          <button type="button" className="linkbtn" onClick={() => setOuvert(true)}>{t('compte.changer')}</button>
        </span>
      </div>
    )
  }

  async function enregistrer() {
    setErreur(null)
    if (nouveau.length < LONGUEUR_MINIMALE) return setErreur(t('password.tooShort', { n: LONGUEUR_MINIMALE }))
    if (nouveau !== confirmation) return setErreur(t('password.mismatch'))
    try {
      await post('/api/auth/password', { current: actuel, next: nouveau })
      setOuvert(false)
      setActuel('')
      setNouveau('')
      setConfirmation('')
      onChange()
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined
      setErreur(
        code === 'actuel_faux' ? t('compte.actuelFaux')
        : code === 'identique' ? t('password.error.same')
        : code === 'trop_court' ? t('password.tooShort', { n: LONGUEUR_MINIMALE })
        : err instanceof ApiError ? err.message
        : t('password.error.generic')
      )
    }
  }

  return (
    <form className="cfg-sous cfg-mdp" onSubmit={(e) => e.preventDefault()}>
      <b>{t('compte.motDePasse')}</b>
      <input className="input-field" type="password" autoComplete="current-password" placeholder={t('compte.actuel')} aria-label={t('compte.actuel')} value={actuel} onChange={(e) => setActuel(e.target.value)} />
      <input className="input-field" type="password" autoComplete="new-password" placeholder={t('password.next')} aria-label={t('password.next')} value={nouveau} onChange={(e) => setNouveau(e.target.value)} />
      <input className="input-field" type="password" autoComplete="new-password" placeholder={t('password.confirm')} aria-label={t('password.confirm')} value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
      <p className="field-help">{t('compte.motDePasseAide', { n: LONGUEUR_MINIMALE })}</p>
      {erreur && <p className="loginerr" role="alert">{erreur}</p>}
      <span className="adresse-form">
        <ActionButton size="sm" variant="primary" disabled={!actuel || !nouveau} onClick={enregistrer}>{t('compte.enregistrer')}</ActionButton>
        <button type="button" className="linkbtn" onClick={() => { setOuvert(false); setErreur(null) }}>{t('common.cancel')}</button>
      </span>
    </form>
  )
}

/** « Télécharger mes données » (issue #175) : profil, réglages, candidatures,
 *  en JSON. Un simple lien : la session suffit, et le navigateur enregistre. */
export function TelechargerDonnees() {
  const t = useT()
  return (
    <div className="cfg-sous">
      <span>
        <a className="linkbtn" href="/api/compte/donnees" download>{t('compte.telecharger')}</a>
      </span>
    </div>
  )
}

/**
 * « Supprimer mon compte » (issue #175), séparé et en rouge, confirmé dans la
 * page par le mot de passe. Ce qui est effacé et gardé est dans
 * services/suppressionCompte.ts ; l'écran le dit en une phrase.
 */
export function SupprimerCompte() {
  const t = useT()
  const [ouvert, setOuvert] = useState(false)
  const [motDePasse, setMotDePasse] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)

  if (!ouvert) {
    return (
      <div className="cfg-sous cfg-separe">
        <div>
          <ActionButton variant="danger" onClick={() => setOuvert(true)}>{t('compte.supprimer')}</ActionButton>
        </div>
      </div>
    )
  }

  async function supprimer() {
    setErreur(null)
    try {
      await post('/api/compte/supprimer', { password: motDePasse })
      // La session est tombée avec le compte : retour à l'écran de connexion.
      window.location.hash = '#/'
      window.location.reload()
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined
      setErreur(code === 'actuel_faux' ? t('compte.actuelFaux') : err instanceof ApiError ? err.message : t('password.error.generic'))
    }
  }

  return (
    <form className="cfg-sous cfg-separe cfg-mdp" onSubmit={(e) => e.preventDefault()}>
      <b className="cfg-danger">{t('compte.supprimer')}</b>
      <p className="field-help">{t('compte.supprimerAide')}</p>
      <input
        className="input-field"
        type="password"
        autoComplete="current-password"
        placeholder={t('compte.actuel')}
        aria-label={t('compte.actuel')}
        value={motDePasse}
        onChange={(e) => setMotDePasse(e.target.value)}
      />
      {erreur && <p className="loginerr" role="alert">{erreur}</p>}
      <span className="adresse-form">
        <ActionButton size="sm" variant="danger" disabled={!motDePasse} onClick={supprimer}>{t('compte.supprimerConfirmer')}</ActionButton>
        <button type="button" className="linkbtn" onClick={() => { setOuvert(false); setErreur(null); setMotDePasse('') }}>{t('common.cancel')}</button>
      </span>
    </form>
  )
}
